import React from 'react';
import classNames from 'classnames';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import { useYScale } from 'utils/components/TimeSeriesChart/yAxis';
import AreaGradient from 'utils/components/TimeSeriesChart/AreaGradient';
import { IntraDayProfileDataItem } from './IntraDayProfileData';
import {
    computeYDomain,
    getChartSeries,
    getYTicks,
    yTickFormat,
} from './intraDayProfileSeries';
import './IntraDayProfileChart.scss';
import { useClickstreamRecordHoverOverLastBucket } from 'widgets/intraDayProfile/hooks';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useIntraDayProfileConfigSlice } from 'widgets/intraDayProfile/store/configuration/configSlice';
import { IntraDayProfileConfiguration } from 'widgets/intraDayProfile/store/configuration/IntraDayProfileConfiguration';
import { useIntradayProfileDimensions } from 'widgets/intraDayProfile/dimensions';

interface IntraDayProfileChartProps {
    width: number;
    dataSeries: IntraDayProfileDataItem[];
}

export default function IntraDayProfileChart(props: IntraDayProfileChartProps) {
    const { width, dataSeries } = props;

    const { widgetKey } = useWidgetContext();
    const { config } = useIntraDayProfileConfigSlice();

    const { chartHeight, chartTop } = useIntradayProfileDimensions();

    useClickstreamRecordHoverOverLastBucket(dataSeries);

    const historicalCumulativeGradientId = `historicalCumulativeGradient-${widgetKey}`;
    const cumulativeGradientId = `cumulativeGradientId-${widgetKey}`;

    const yScale = useYScale(computeYDomain(dataSeries));
    const series = getChartSeries(
        dataSeries,
        width,
        historicalCumulativeGradientId,
        cumulativeGradientId
    );
    const yTicks = getYTicks();

    const legendDisableClasses = Object.keys(config)
        .filter((key) => key.startsWith('hide'))
        .filter((key) => !key.endsWith('Legend'))
        .filter((key) => config[key as keyof IntraDayProfileConfiguration])
        .map(
            (key) =>
                `disable-${key.substring(4, 5).toLowerCase()}${key.substring(
                    5
                )}`
        );

    return (
        <div
            className={classNames(
                'IntraDayProfileChart',
                ...legendDisableClasses
            )}
            style={{
                height: chartHeight + 'px',
                width: width + 'px',
                top: chartTop + 'px',
            }}
        >
            <TimeSeriesChart
                id="IntraDayProfileChart"
                height={chartHeight}
                data={dataSeries}
                series={series}
                displayXAxis={true}
                yScale={yScale}
                yTicks={yTicks}
                yTickFormat={yTickFormat}
            />
            <AreaGradient
                identifier={historicalCumulativeGradientId}
                className="historicalCumulativeGradient"
                startOffset="0%"
                stopOffset="100%"
            />
            <AreaGradient
                identifier={cumulativeGradientId}
                className="cumulativeTradedGradient"
                startOffset="0%"
                stopOffset="100%"
            />
        </div>
    );
}
